import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight, FileText, FilePlus2, ShieldCheck,
  GitBranch, UploadCloud, LayoutTemplate, Download,
  TrendingUp, LineChart, BarChart3, Mail, Phone, MapPin, Linkedin,
} from "lucide-react";
import { CvShell } from "@/components/cv/CvShell";
import { CV_LANDING, CV_ENTRIES, CV_ENTRIES_NOTE, CV_STEPS, CV_FAQ } from "@/components/cv/cvContent";

const STEP_ICONS = [GitBranch, UploadCloud, LayoutTemplate, Download];

// Nazwy firm są wyłącznie przykładowymi placeholderami (brak realnej weryfikacji).
const EMPLOYERS = [
  { name: "GrowthLab", role: "Specjalista ds. PPC", loc: "Warszawa · Hybrydowo", Icon: TrendingUp },
  { name: "DigitalPeak", role: "Performance Marketing Specialist", loc: "Kraków · Zdalnie", Icon: LineChart },
  { name: "BrandCraft", role: "Marketing Manager", loc: "Wrocław · Hybrydowo", Icon: BarChart3 },
];

const CvDocument = () => (
  <div className="relative">
    <div className="absolute -right-3 top-3 hidden h-full w-full rounded-sm bg-white/10 md:block" aria-hidden />
    <div data-testid="cv-doc-preview" className="relative rounded-sm bg-[#f4f2ec] p-6 text-[#14140f] shadow-2xl">
      <header className="border-b border-black/15 pb-4">
        <h3 className="font-display text-2xl font-extrabold tracking-tight">JAN NOWAK</h3>
        <p className="text-[13px] text-black/60">Specjalista ds. marketingu cyfrowego</p>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-black/60">
          <span className="inline-flex items-center gap-1"><Mail size={10} /> jan.nowak@email.com</span>
          <span className="inline-flex items-center gap-1"><Phone size={10} /> +48 600 123 456</span>
          <span className="inline-flex items-center gap-1"><MapPin size={10} /> Warszawa, Polska</span>
          <span className="inline-flex items-center gap-1"><Linkedin size={10} /> linkedin.com/in/jannowak</span>
        </div>
      </header>

      <section className="mt-4">
        <p className="text-[11px] font-bold tracking-wide text-black/70">PODSUMOWANIE</p>
        <p className="mt-1 text-[10.5px] leading-relaxed text-black/70">
          Specjalista ds. marketingu cyfrowego z 5-letnim doświadczeniem w planowaniu i realizacji kampanii online.
          Skutecznie zwiększam widoczność marek i konwersję, łącząc analizę danych z kreatywnymi rozwiązaniami.
        </p>
      </section>

      <section className="mt-4">
        <p className="text-[11px] font-bold tracking-wide text-black/70">DOŚWIADCZENIE</p>
        <div className="mt-1">
          <p className="text-[11px] font-semibold">Specjalista ds. marketingu cyfrowego</p>
          <p className="text-[9.5px] text-black/50">ABC Sp. z o.o. · 03.2021 – obecnie</p>
          <ul className="mt-1 space-y-0.5 text-[10px] text-black/70">
            <li>• Prowadzenie kampanii Google Ads i Meta Ads</li>
            <li>• Analiza danych i optymalizacja działań marketingowych</li>
            <li>• Tworzenie treści i współpraca z zespołem sprzedaży</li>
            <li>• Wzrost konwersji o 35% r/r</li>
          </ul>
        </div>
        <div className="mt-2">
          <p className="text-[11px] font-semibold">Młodszy specjalista ds. marketingu</p>
          <p className="text-[9.5px] text-black/50">XYZ Group · 06.2019 – 02.2021</p>
          <ul className="mt-1 space-y-0.5 text-[10px] text-black/70">
            <li>• Wsparcie w realizacji kampanii online</li>
            <li>• Tworzenie raportów i analiz efektywności</li>
            <li>• Obsługa social media i newsletterów</li>
          </ul>
        </div>
      </section>

      <section className="mt-4">
        <p className="text-[11px] font-bold tracking-wide text-black/70">UMIEJĘTNOŚCI</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {["Google Ads", "Meta Ads", "SEO", "Analityka", "GA4", "Excel"].map((s) => (
            <span key={s} className="rounded bg-black/[0.07] px-2 py-0.5 text-[9.5px] text-black/70">{s}</span>
          ))}
        </div>
      </section>

      <section className="mt-4">
        <p className="text-[11px] font-bold tracking-wide text-black/70">WYKSZTAŁCENIE</p>
        <p className="mt-1 text-[10.5px] font-semibold">Uniwersytet Warszawski</p>
        <p className="text-[9.5px] text-black/50">Zarządzanie · 2016 – 2019</p>
      </section>
    </div>
  </div>
);

const EmployersPanel = () => (
  <div data-testid="cv-employers-panel" className="rounded-panel border border-brand-line bg-brand-surface p-4">
    <p className="mb-3 text-sm font-semibold text-brand-text">Dopasowani pracodawcy</p>
    <div className="space-y-2.5">
      {EMPLOYERS.map(({ name, role, loc, Icon }) => (
        <div key={name} className="flex items-center gap-3 rounded-card border border-brand-line bg-brand-elevated p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-field bg-brand-bg text-brand-accent">
            <Icon size={18} strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-brand-text">{name}</p>
            <p className="text-[11px] leading-tight text-brand-sec">{role}</p>
            <p className="truncate text-[10.5px] text-brand-muted">{loc}</p>
          </div>
          <ArrowRight size={15} className="shrink-0 text-brand-muted" />
        </div>
      ))}
    </div>
    <div className="mt-3 flex flex-wrap gap-2">
      {["PDF", "DOCX", "List motywacyjny"].map((c) => (
        <span key={c} className="inline-flex items-center gap-1 rounded-field border border-brand-line px-2.5 py-1.5 text-[11px] text-brand-sec">
          <FileText size={12} className="text-brand-accent" /> {c}
        </span>
      ))}
    </div>
  </div>
);

export default function CvLanding() {
  const { scenarioSlug } = useParams();
  const navigate = useNavigate();

  return (
    <CvShell showNav={false}>
      <div data-testid="cv-landing" className="pb-6">
        {/* HERO — dwie kolumny, oba wejścia bez przewijania */}
        <section className="grid items-start gap-10 pt-2 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.1fr)] lg:gap-12">
          {/* Lewa kolumna */}
          <div>
            <span className="text-sm font-semibold uppercase tracking-[0.14em] text-brand-accent">
              {CV_LANDING.eyebrow}{scenarioSlug ? ` · ${scenarioSlug}` : ""}
            </span>
            <h1 className="mt-4 max-w-[15ch] font-display text-3xl font-bold leading-[1.1] tracking-tight text-brand-text [text-wrap:balance] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.08]">
              {CV_LANDING.title}
            </h1>
            <p className="mt-5 max-w-xl text-base text-brand-sec md:text-lg">{CV_LANDING.lead}</p>

            {/* Dwa wejścia */}
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {CV_ENTRIES.map((entry) => (
                <div
                  key={entry.key}
                  data-testid={`cv-entry-${entry.key}`}
                  className={`flex flex-col rounded-panel border bg-brand-surface p-5 ${
                    entry.primary ? "border-brand-accent/70" : "border-brand-line"
                  }`}
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-card border border-brand-line text-brand-accent">
                    {entry.key === "nowe" ? <FilePlus2 size={20} /> : <FileText size={20} />}
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-brand-text">{entry.title}</h3>
                  <p className="mt-1 text-sm text-brand-sec">{entry.text}</p>
                  <button
                    type="button"
                    data-testid={`cv-entry-${entry.key}-cta`}
                    onClick={() => navigate(entry.to)}
                    className={`mt-4 inline-flex items-center justify-center gap-2 rounded-button px-4 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
                      entry.primary
                        ? "bg-brand-accent text-brand-on"
                        : "border border-brand-accent/50 text-brand-accent"
                    }`}
                  >
                    {entry.button} <ArrowRight size={15} />
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-4 flex items-center gap-2 text-[13px] text-brand-muted">
              <ShieldCheck size={15} className="text-brand-accent" /> {CV_ENTRIES_NOTE}
            </p>
          </div>

          {/* Prawa kolumna: podgląd CV + panel pracodawców */}
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <div className="md:flex-1">
              <CvDocument />
            </div>
            <div className="md:w-[300px] md:shrink-0">
              <EmployersPanel />
            </div>
          </div>
        </section>

        {/* Jak to działa — cztery uproszczone kroki */}
        <section id="jak-to-dziala" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-2xl font-bold text-brand-text md:text-3xl">Jak to działa</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {CV_STEPS.map((step, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <div key={i} data-testid="cv-step" className="relative rounded-panel border border-brand-line bg-brand-surface p-5">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-card border border-brand-line text-brand-accent">
                      <Icon size={20} strokeWidth={1.75} />
                    </span>
                    <span className="font-display text-sm font-semibold text-brand-muted">{i + 1}</span>
                  </div>
                  <h3 className="mt-4 font-display text-base font-semibold text-brand-text">{step.title}</h3>
                  <p className="mt-1.5 text-sm text-brand-sec">{step.text}</p>
                  {i < CV_STEPS.length - 1 && (
                    <ArrowRight size={18} className="absolute -right-3 top-1/2 hidden -translate-y-1/2 text-brand-line lg:block" />
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Szablony teaser */}
        <section className="mt-16">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold text-brand-text md:text-3xl">Szablony CV</h2>
            <Link to="/cv/szablony" data-testid="cv-landing-templates-link" className="text-sm font-medium text-brand-accent hover:underline">
              Zobacz galerię
            </Link>
          </div>
          <p className="mt-2 max-w-2xl text-sm text-brand-sec">
            Treść CV jest niezależna od wyglądu — szablon, kolor i układ zmienisz w dowolnym momencie bez utraty danych.
          </p>
        </section>

        {/* FAQ */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold text-brand-text md:text-3xl">Najczęstsze pytania</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {CV_FAQ.map((item, i) => (
              <div key={i} data-testid="cv-faq-item" className="rounded-card border border-brand-line bg-brand-surface p-5">
                <h3 className="font-medium text-brand-text">{item.q}</h3>
                <p className="mt-2 text-sm text-brand-sec">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </CvShell>
  );
}
